import type { FC } from "react";
import type { ScotomaSpot } from "shared/schema/src/types";
import { AnisoconiaTool } from "./AnisoconiaTool";
import { BlurrinessTool } from "./BlurrinessTool";
import { ColourShiftTool } from "./ColourShiftTool";
import { ContrastTool } from "./ContrastTool";
import { DiplopiaTool } from "./DiplopiaTool";
import { FieldDefectTool } from "./FieldDefectTool";
import { ImageSizeTool } from "./ImageSizeTool";
import { MetamorphopsiaTool } from "./MetamorphopsiaTool";
import { RotationTool } from "./RotationTool";
import { ScotomaTool } from "./ScotomaTool";
import { DistortionControls } from "./controls/DistortionControls";
import { VacuumControls } from "./controls/VacuumControls";

type ToolKey =
	| "distortion"
	| "vacuum"
	| "imageBrightness"
	| "contrast"
	| "blurriness"
	| "scotoma"
	| "colourShift"
	| "rotation"
	| "diplopia"
	| "metamorphopsia"
	| "anisoconia"
	| "fieldDefect";

interface ToolControlPanelProps {
	currentTool: ToolKey | null;
	eyeState: any; // TODO: Type this properly with EyeState interface
	// F12: which distortion slice the tool edits — "amslerDistortion" in Amsler
	// mode, "distortion" otherwise. Decided by gridMode in session.tsx.
	distortionField: "distortion" | "amslerDistortion";
	selectedScotomaIndex: number | null;
	onSelectScotoma: (index: number | null) => void;
	onEyeStateChange: (updater: (prev: any) => any) => void;
	// Canvas refs for distortion/vacuum immediate toggle
	visibleCanvasRef: React.RefObject<HTMLCanvasElement | null>;
	imageLoaded: boolean;
	referenceImage: HTMLImageElement | null;
	imageWidth: number;
	imageHeight: number;
	showGrid: boolean;
	regionDrawMode: "none" | "drawing";
	regionPoints: Array<{ x: number; y: number }>;
	onStartDrawing: () => void;
	onUndoPoint: () => void;
	onCancelDrawing: () => void;
	onClearRegion: () => void;
	fieldDefectMode: "tunnel" | "curtain";
	curtainAwaitingSecondTap: boolean;
	onFieldDefectModeChange: (mode: "tunnel" | "curtain") => void;
	onFlipFieldDefectSide: () => void;
	onClearFieldDefect: () => void;
	disabled?: boolean;
}

export const ToolControlPanel: FC<ToolControlPanelProps> = ({
	currentTool,
	eyeState,
	distortionField,
	selectedScotomaIndex,
	onSelectScotoma,
	onEyeStateChange,
	visibleCanvasRef,
	imageLoaded,
	referenceImage,
	imageWidth,
	imageHeight,
	showGrid,
	regionDrawMode,
	regionPoints,
	onStartDrawing,
	onUndoPoint,
	onCancelDrawing,
	onClearRegion,
	fieldDefectMode,
	curtainAwaitingSecondTap,
	onFieldDefectModeChange,
	onFlipFieldDefectSide,
	onClearFieldDefect,
	disabled,
}) => {
	const content = (() => {
		switch (currentTool) {
			case "distortion":
				return (
					<DistortionControls
						power={eyeState[distortionField].power}
						range={eyeState[distortionField].range}
						showImage={eyeState[distortionField].showImage}
						onPowerChange={(power) =>
							onEyeStateChange((prev) => ({
								...prev,
								[distortionField]: { ...prev[distortionField], power },
							}))
						}
						onRangeChange={(range) =>
							onEyeStateChange((prev) => ({
								...prev,
								[distortionField]: { ...prev[distortionField], range },
							}))
						}
						onShowImageChange={(showImage) =>
							onEyeStateChange((prev) => ({
								...prev,
								[distortionField]: { ...prev[distortionField], showImage },
							}))
						}
						onClearPoints={() =>
							onEyeStateChange((prev) => ({
								...prev,
								[distortionField]: { ...prev[distortionField], points: [] },
							}))
						}
						visibleCanvasRef={visibleCanvasRef}
						imageLoaded={imageLoaded}
						referenceImage={referenceImage}
						imageWidth={imageWidth}
						imageHeight={imageHeight}
						showGrid={showGrid}
					/>
				);

			case "vacuum":
				return (
					<VacuumControls
						power={eyeState.vacuum.power}
						range={eyeState.vacuum.range}
						showImage={eyeState.distortion.showImage}
						onPowerChange={(power) =>
							onEyeStateChange((prev) => ({
								...prev,
								vacuum: { ...prev.vacuum, power },
							}))
						}
						onRangeChange={(range) =>
							onEyeStateChange((prev) => ({
								...prev,
								vacuum: { ...prev.vacuum, range },
							}))
						}
						onShowImageChange={(showImage) =>
							onEyeStateChange((prev) => ({
								...prev,
								distortion: { ...prev.distortion, showImage },
							}))
						}
						onClearPoints={() =>
							onEyeStateChange((prev) => ({
								...prev,
								vacuum: { ...prev.vacuum, points: [] },
							}))
						}
						visibleCanvasRef={visibleCanvasRef}
						imageLoaded={imageLoaded}
						referenceImage={referenceImage}
						imageWidth={imageWidth}
						imageHeight={imageHeight}
						showGrid={showGrid}
					/>
				);

			case "imageBrightness":
				return (
					<ImageSizeTool
						magnification={eyeState.imageBrightness.value}
						roiValue={eyeState.imageBrightness.roiValue ?? 1}
						onChange={(value) =>
							onEyeStateChange((prev) => ({
								...prev,
								imageBrightness: { ...prev.imageBrightness, value },
							}))
						}
						onRoiValueChange={(roiValue) =>
							onEyeStateChange((prev) => ({
								...prev,
								imageBrightness: { ...prev.imageBrightness, roiValue },
							}))
						}
						effectRegion={eyeState.imageBrightness.effectRegion}
						regionDrawMode={regionDrawMode}
						regionPoints={regionPoints}
						onStartDrawing={onStartDrawing}
						onUndoPoint={onUndoPoint}
						onCancelDrawing={onCancelDrawing}
						onClearRegion={onClearRegion}
					/>
				);

			case "contrast":
				return (
					<ContrastTool
						level={eyeState.contrast.level}
						roiLevel={eyeState.contrast.roiLevel ?? 1}
						preset={eyeState.contrast.preset}
						mode={eyeState.contrast.mode}
						onChange={(value) =>
							onEyeStateChange((prev) => ({
								...prev,
								contrast: {
									...value,
									roiLevel: prev.contrast.roiLevel,
									effectRegion: prev.contrast.effectRegion,
								},
							}))
						}
						onRoiLevelChange={(roiLevel) =>
							onEyeStateChange((prev) => ({
								...prev,
								contrast: { ...prev.contrast, roiLevel },
							}))
						}
						effectRegion={eyeState.contrast.effectRegion}
						regionDrawMode={regionDrawMode}
						regionPoints={regionPoints}
						onStartDrawing={onStartDrawing}
						onUndoPoint={onUndoPoint}
						onCancelDrawing={onCancelDrawing}
						onClearRegion={onClearRegion}
					/>
				);

			case "blurriness":
				return (
					<BlurrinessTool
						sigma={eyeState.blurriness.sigma}
						roiSigma={eyeState.blurriness.roiSigma ?? 0}
						onChange={(value) =>
							onEyeStateChange((prev) => ({
								...prev,
								blurriness: {
									...value,
									roiSigma: prev.blurriness.roiSigma,
									effectRegion: prev.blurriness.effectRegion,
								},
							}))
						}
						onRoiSigmaChange={(roiSigma) =>
							onEyeStateChange((prev) => ({
								...prev,
								blurriness: { ...prev.blurriness, roiSigma },
							}))
						}
						effectRegion={eyeState.blurriness.effectRegion}
						regionDrawMode={regionDrawMode}
						regionPoints={regionPoints}
						onStartDrawing={onStartDrawing}
						onUndoPoint={onUndoPoint}
						onCancelDrawing={onCancelDrawing}
						onClearRegion={onClearRegion}
					/>
				);

			case "scotoma":
				return (
					<ScotomaTool
						spots={eyeState.scotoma}
						selectedIndex={selectedScotomaIndex}
						onSelect={onSelectScotoma}
						onClearAll={() => {
							onEyeStateChange((prev) => ({ ...prev, scotoma: [] }));
							onSelectScotoma(null);
						}}
						onUpdateSelected={(patch) =>
							onEyeStateChange((prev) => ({
								...prev,
								scotoma: prev.scotoma.map((s: ScotomaSpot, i: number) =>
									i === selectedScotomaIndex ? { ...s, ...patch } : s,
								),
							}))
						}
					/>
				);

			case "fieldDefect":
				return (
					<FieldDefectTool
						mode={fieldDefectMode}
						onModeChange={onFieldDefectModeChange}
						hasDefect={!!eyeState.fieldDefect}
						curtainAwaitingSecondTap={curtainAwaitingSecondTap}
						onFlipSide={onFlipFieldDefectSide}
						onClear={onClearFieldDefect}
						regionDrawMode={regionDrawMode}
						regionPointCount={regionPoints.length}
						onStartTunnelDraw={onStartDrawing}
						onUndoPoint={onUndoPoint}
						onCancelDrawing={onCancelDrawing}
					/>
				);

			case "colourShift":
				return (
					<ColourShiftTool
						hue={eyeState.colourShift.hue}
						saturation={eyeState.colourShift.saturation}
						preset={eyeState.colourShift.preset}
						onChange={(value) =>
							onEyeStateChange((prev) => ({ ...prev, colourShift: value }))
						}
					/>
				);

			case "rotation":
				return (
					<RotationTool
						angle={eyeState.rotation.angle}
						onChange={(value) =>
							onEyeStateChange((prev) => ({ ...prev, rotation: value }))
						}
					/>
				);

			case "diplopia":
				return (
					<DiplopiaTool
						enabled={eyeState.diplopia.enabled}
						offset={eyeState.diplopia.offset}
						overlap={eyeState.diplopia.overlap}
						onChange={(value) =>
							onEyeStateChange((prev) => ({ ...prev, diplopia: value }))
						}
					/>
				);

			case "metamorphopsia":
				// Auto-enable metamorphopsia when tool is selected
				if (!eyeState.metamorphopsia.enabled) {
					onEyeStateChange((prev) => ({
						...prev,
						metamorphopsia: {
							...prev.metamorphopsia,
							enabled: true,
						},
					}));
				}
				return (
					<MetamorphopsiaTool
						enabled={eyeState.metamorphopsia.enabled}
						amplitude={eyeState.metamorphopsia.amplitude}
						frequency={eyeState.metamorphopsia.frequency}
						direction={eyeState.metamorphopsia.direction}
						mode={eyeState.metamorphopsia.mode ?? "whole"}
						bridge={eyeState.metamorphopsia.bridge ?? 0.5}
						hasRegion={eyeState.metamorphopsia.region != null}
						onChange={(patch) =>
							onEyeStateChange((prev) => ({
								...prev,
								metamorphopsia: { ...prev.metamorphopsia, ...patch },
							}))
						}
						onClearRegion={() =>
							onEyeStateChange((prev) => ({
								...prev,
								metamorphopsia: { ...prev.metamorphopsia, region: null },
							}))
						}
					/>
				);

			case "anisoconia":
				return (
					<AnisoconiaTool
						mode={eyeState.anisoconia.mode ?? "whole"}
						headScale={eyeState.anisoconia.headScale}
						scale={eyeState.anisoconia.scale ?? 1}
						bridge={eyeState.anisoconia.bridge ?? 0.5}
						hasRegion={eyeState.anisoconia.region != null}
						onChange={(patch) =>
							onEyeStateChange((prev) => ({
								...prev,
								anisoconia: { ...prev.anisoconia, ...patch },
							}))
						}
						onClearRegion={() =>
							onEyeStateChange((prev) => ({
								...prev,
								anisoconia: { ...prev.anisoconia, region: null },
							}))
						}
					/>
				);

			default:
				return null;
		}
	})();

	if (disabled) {
		return <div className="pointer-events-none opacity-60">{content}</div>;
	}

	return content;
};
